<!-- <template>
  <div
    :class="[
      'searchable-select',
      {
        'with-action': !!$slots.action,
        'is-open': open,
        'is-disabled': disabled,
      },
    ]"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <input
      ref="inputEl"
      class="search-input"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      v-model="query"
      role="combobox"
      :aria-expanded="String(open)"
      :aria-controls="listboxId"
      :aria-activedescendant="activeId || null"
      aria-autocomplete="list"
      autocomplete="off"
      @keydown="onKeydown"
      @input="onInput"
      @focus="openList"
    />

    <button
      v-if="$slots.action"
      type="button"
      class="combo-action-btn"
      :disabled="disabled"
      @mousedown.prevent
      @click="$emit('action')"
      aria-label="azione"
    >
      <slot name="action" />
    </button>

    <ul
      v-show="open"
      ref="listEl"
      class="search-results"
      :id="listboxId"
      role="listbox"
      @mousedown.prevent
      @touchstart.prevent
    >
      <li
        v-for="(opt, idx) in items"
        :key="valueKey(opt, idx)"
        class="combo-option"
        :class="{ 'is-active': idx === activeIndex }"
        role="option"
        :id="optionId(idx)"
        :aria-selected="String(idx === activeIndex)"
        @mouseenter="setActive(idx)"
        @mousemove="setActive(idx)"
        @click="choose(idx)"
        @touchend.prevent="choose(idx)"
      >
        <slot name="option" :option="opt" :label="labelKey(opt)" />
        <template v-if="!$slots.option">{{ labelKey(opt) }}</template>
      </li>

      <li
        v-if="!loading && items.length === 0"
        class="combo-empty"
        aria-disabled="true"
      >
        {{ emptyText }}
      </li>
      <li v-if="loading" class="combo-loading" aria-disabled="true">
        {{ loadingText }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SearchableCombo",
  props: {
    modelValue: [String, Number, Object, null],
    items: { type: Array, default: () => [] },
    labelKey: {
      type: Function,
      default: (o) => o?.label ?? o?.name ?? o?.title ?? "",
    },
    valueKey: {
      type: Function,
      default: (o) => o?.id ?? o?.value ?? JSON.stringify(o),
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    emptyText: { type: String, default: "Nessun risultato" },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "Caricamento…" },
    initialQuery: { type: String, default: "" },
  },
  emits: [
    "update:modelValue",
    "search",
    "select",
    "action",
    "query-change",
    "cleared",
  ],
  data() {
    return {
      open: false,
      query: this.initialQuery,
      activeIndex: -1,
      listboxId: `lb-${Math.random().toString(36).slice(2, 8)}`,
    };
  },
  computed: {
    activeId() {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    },
  },
  watch: {
    initialQuery(q) {
      this.query = q || "";
    },
    // 👇 quando il parent azzera v-model, svuoto input e chiudo lista
    modelValue(v) {
      if (v === null || v === "" || typeof v === "undefined") {
        this.query = "";
        this.closeList();
        this.$emit("cleared");
      }
    },
  },
  mounted() {
    const st = window.getComputedStyle(this.$refs.inputEl);
    if (parseFloat(st.fontSize) < 16)
      this.$refs.inputEl.style.fontSize = "16px"; // iOS zoom fix
  },
  methods: {
    clear() {
      this.query = "";
      this.closeList();
      this.$emit("update:modelValue", null);
      this.$emit("cleared");
    },
    optionId(i) {
      return `${this.listboxId}-opt-${i}`;
    },
    openList() {
      if (this.disabled) return;
      this.open = true;
      this.$nextTick(this.ensureVisible);
    },
    closeList() {
      this.open = false;
      this.activeIndex = -1;
    },
    onFocusIn() {},
    onFocusOut(e) {
      const goingTo = e.relatedTarget;
      if (!goingTo || !this.$el.contains(goingTo)) this.closeList();
    },
    onInput() {
      this.$emit("query-change", this.query);
      this.$emit("search", this.query?.trim() || "");
      this.openList();
      this.activeIndex = this.items.length ? 0 : -1;
    },
    onKeydown(e) {
      if (!this.open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        this.openList();
        e.preventDefault();
        this.activeIndex = this.items.length ? 0 : -1;
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          if (this.items.length) {
            this.activeIndex = (this.activeIndex + 1) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "ArrowUp":
          if (this.items.length) {
            this.activeIndex =
              (this.activeIndex - 1 + this.items.length) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "Enter":
          if (this.open && this.activeIndex >= 0) {
            this.choose(this.activeIndex);
            e.preventDefault();
          }
          break;
        case "Escape":
          this.closeList();
          e.preventDefault();
          break;
      }
    },
    setActive(i) {
      this.activeIndex = i;
    },
    choose(i) {
      const opt = this.items[i];
      if (!opt) return;
      const val = this.valueKey(opt, i);
      this.$emit("update:modelValue", val);
      this.$emit("select", opt);
      this.query = this.labelKey(opt);
      this.closeList();
      this.$nextTick(() => this.$refs.inputEl?.focus());
    },
    ensureVisible() {
      const list = this.$refs.listEl;
      if (!list || this.activeIndex < 0) return;
      const el = list.children[this.activeIndex];
      if (!el) return;
      const top = el.offsetTop,
        bottom = top + el.offsetHeight;
      const viewTop = list.scrollTop,
        viewBottom = viewTop + list.clientHeight;
      if (top < viewTop) list.scrollTop = top;
      else if (bottom > viewBottom) list.scrollTop = bottom - list.clientHeight;
    },
  },
};
</script> -->

<template>
  <div
    :class="[
      'searchable-select',
      {
        'with-action': !!$slots.action,
        'is-open': open,
        'is-disabled': disabled,
      },
    ]"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <input
      ref="inputEl"
      class="search-input"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      v-model="query"
      role="combobox"
      :aria-expanded="String(open)"
      :aria-controls="listboxId"
      :aria-activedescendant="activeId || null"
      aria-autocomplete="list"
      autocomplete="off"
      @keydown="onKeydown"
      @input="onInput"
      @focus="openList"
    />

    <button
      v-if="$slots.action"
      type="button"
      class="combo-action-btn"
      :disabled="disabled"
      @mousedown.prevent
      @click="$emit('action')"
      aria-label="azione"
    >
      <slot name="action" />
    </button>

    <ul
      v-show="open"
      ref="listEl"
      class="search-results"
      :id="listboxId"
      role="listbox"
      @mousedown.prevent
      @touchstart.prevent
    >
      <li
        v-for="(opt, idx) in items"
        :key="valueKey(opt, idx)"
        class="combo-option"
        :class="{ 'is-active': idx === activeIndex }"
        role="option"
        :id="optionId(idx)"
        :aria-selected="String(idx === activeIndex)"
        @mouseenter="setActive(idx)"
        @mousemove="setActive(idx)"
        @click="choose(idx)"
        @touchend.prevent="choose(idx)"
      >
        <slot name="option" :option="opt" :label="labelKey(opt)" />
        <template v-if="!$slots.option">{{ labelKey(opt) }}</template>
      </li>

      <li
        v-if="!loading && items.length === 0"
        class="combo-empty"
        aria-disabled="true"
      >
        {{ emptyText }}
      </li>
      <li v-if="loading" class="combo-loading" aria-disabled="true">
        {{ loadingText }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SearchableCombo",
  props: {
    modelValue: [String, Number, Object, null],
    /** 👇 nuovo: testo controllato dal parent */
    text: { type: String, default: "" },
    items: { type: Array, default: () => [] },
    labelKey: {
      type: Function,
      default: (o) => o?.label ?? o?.name ?? o?.title ?? "",
    },
    valueKey: {
      type: Function,
      default: (o) => o?.id ?? o?.value ?? JSON.stringify(o),
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    emptyText: { type: String, default: "Nessun risultato" },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "Caricamento…" },
    /** legacy, resta supportato come fallback */
    initialQuery: { type: String, default: "" },
  },
  emits: [
    "update:modelValue",
    /** 👇 nuovo: consente v-model:text */
    "update:text",
    "search",
    "select",
    "action",
    "query-change",
    "cleared",
  ],
  data() {
    return {
      open: false,
      /** 👇 priorità: prop `text` → `initialQuery` */
      query: this.text || this.initialQuery,
      activeIndex: -1,
      listboxId: `lb-${Math.random().toString(36).slice(2, 8)}`,
    };
  },
  computed: {
    activeId() {
      return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    },
  },
  watch: {
    /** se cambia il testo controllato dal parent, aggiornalo */
    text(newText) {
      if ((newText || "") !== (this.query || "")) {
        this.query = newText || "";
      }
    },
    /** compat: se qualcuno usa ancora initialQuery */
    initialQuery(q) {
      if (!this.text) this.query = q || "";
    },
    /** quando il parent azzera il v-model, svuota anche il testo e chiudi */
    modelValue(v) {
      if (v === null || v === "" || typeof v === "undefined") {
        this.query = "";
        this.$emit("update:text", "");
        this.closeList();
        this.$emit("cleared");
      }
    },
  },
  mounted() {
    const st = window.getComputedStyle(this.$refs.inputEl);
    if (parseFloat(st.fontSize) < 16)
      this.$refs.inputEl.style.fontSize = "16px"; // iOS zoom fix
  },
  methods: {
    clear() {
      this.query = "";
      this.$emit("update:text", "");
      this.closeList();
      this.$emit("update:modelValue", null);
      this.$emit("cleared");
    },
    optionId(i) {
      return `${this.listboxId}-opt-${i}`;
    },
    openList() {
      if (this.disabled) return;
      const wasOpen = this.open;
      this.open = true;
      if (!wasOpen && !this.query) {
        this.$emit("search", "");
      }
      this.$nextTick(this.ensureVisible);
    },
    closeList() {
      this.open = false;
      this.activeIndex = -1;
    },
    onFocusIn() {},
    onFocusOut(e) {
      const goingTo = e.relatedTarget;
      if (!goingTo || !this.$el.contains(goingTo)) this.closeList();
    },
    onInput() {
      /** 👉 propaga SEMPRE il testo verso l’esterno */
      this.$emit("update:text", this.query);
      this.$emit("query-change", this.query);
      this.$emit("search", this.query?.trim() || "");
      this.openList();
      this.activeIndex = this.items.length ? 0 : -1;
    },
    onKeydown(e) {
      if (!this.open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        this.openList();
        e.preventDefault();
        this.activeIndex = this.items.length ? 0 : -1;
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          if (this.items.length) {
            this.activeIndex = (this.activeIndex + 1) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "ArrowUp":
          if (this.items.length) {
            this.activeIndex =
              (this.activeIndex - 1 + this.items.length) % this.items.length;
            this.ensureVisible();
          }
          e.preventDefault();
          break;
        case "Enter":
          if (this.open && this.activeIndex >= 0) {
            this.choose(this.activeIndex);
            e.preventDefault();
          }
          break;
        case "Escape":
          this.closeList();
          e.preventDefault();
          break;
      }
    },
    setActive(i) {
      this.activeIndex = i;
    },
    choose(i) {
      const opt = this.items[i];
      if (!opt) return;
      const val = this.valueKey(opt, i);
      const lbl = this.labelKey(opt);
      this.$emit("update:modelValue", val);
      this.$emit("select", opt);
      /** 👉 imposta e propaga il testo “ufficiale” dell’opzione scelta */
      this.query = lbl;
      this.$emit("update:text", lbl);
      this.closeList();
      this.$nextTick(() => this.$refs.inputEl?.focus());
    },
    ensureVisible() {
      const list = this.$refs.listEl;
      if (!list || this.activeIndex < 0) return;
      const el = list.children[this.activeIndex];
      if (!el) return;
      const top = el.offsetTop,
        bottom = top + el.offsetHeight;
      const viewTop = list.scrollTop,
        viewBottom = viewTop + list.clientHeight;
      if (top < viewTop) list.scrollTop = top;
      else if (bottom > viewBottom) list.scrollTop = bottom - list.clientHeight;
    },
  },
};
</script>

<style scoped>
/* wrapper come il tuo .searchable-select globale, ma qui gestisco il posizionamento */
.searchable-select {
  position: relative;
  width: 100%;
}

.search-input {
  font-size: 12px !important;
  border: 1px solid #ccc;
  border-radius: 4px;
  line-height: 1.6;
  box-sizing: border-box;
}

/* allineo il dropdown allo stile esistente (.search-results del tuo CSS) ma lo rendo assoluto */
.search-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  z-index: 2147483647;
  -webkit-overflow-scrolling: touch;
}

/* rendi selezionabile con hover/attivo anche se sono <li> e non <option> */
.search-results .combo-option {
  padding: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-results .combo-option.highlighted,
.search-results .combo-option.is-active,
.search-results .combo-option:hover {
  background-color: #f5f5f5;
}

/* messaggi empty/loading */
.combo-empty,
.combo-loading {
  padding: 8px;
  font-size: 12px;
  color: #64748b;
}
</style>
